/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

// Read SECONDARY_350 (absent from the typed appColors inference) via a cast,
// matching the player form's skeleton.
const palette = colors as unknown as Record<string, string>

/**
 * Cold-load placeholder for the invitation signup screen.
 *
 * Mirrors the real layout — logo, title, context line, two field rows and the
 * submit button — so nothing shifts when the invitation resolves. Decorative,
 * so it is hidden from assistive tech.
 */
const InviteSignupSkeleton = () => (
  <div css={styles.container} aria-hidden='true'>
    <div css={styles.header}>
      <div css={[styles.bar, styles.logo]} />
      <div css={styles.headerCopy}>
        <div css={[styles.bar, styles.title]} />
        <div css={[styles.bar, styles.context]} />
      </div>
    </div>

    <div css={styles.form}>
      <div css={styles.row}>
        <div css={styles.field}>
          <div css={[styles.bar, styles.label]} />
          <div css={[styles.bar, styles.control]} />
        </div>
        <div css={styles.field}>
          <div css={[styles.bar, styles.label]} />
          <div css={[styles.bar, styles.control]} />
        </div>
      </div>

      <div css={styles.row}>
        <div css={styles.field}>
          <div css={[styles.bar, styles.label]} />
          <div css={[styles.bar, styles.control]} />
        </div>
        <div css={styles.field}>
          <div css={[styles.bar, styles.label]} />
          <div css={[styles.bar, styles.control]} />
        </div>
      </div>

      <div css={[styles.bar, styles.control]} />
      <div css={[styles.bar, styles.terms]} />
    </div>
  </div>
)

export default InviteSignupSkeleton

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const styles = {
  /* Matches the real column's width so nothing shifts when the invite lands. */
  container: css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    max-width: 650px;
  `,
  header: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
  `,
  headerCopy: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  row: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  bar: css`
    border-radius: 8px;
    background-color: ${palette.SECONDARY_350};
    animation: ${pulse} 1.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  logo: css`
    height: 48px;
    width: 214px;
  `,
  title: css`
    height: 36px;
    width: 85%;
  `,
  context: css`
    height: 20px;
    width: 70%;
  `,
  label: css`
    height: 14px;
    width: 60%;
  `,
  control: css`
    height: 40px;
  `,
  terms: css`
    height: 16px;
    width: 70%;
    align-self: center;
  `,
}
